/**
 * useRosterLeadId — track:browser-session-notification-identity, Part 2.
 *
 * Resolves the confirmed human_lead's derived agent-id client-side, for UI
 * files that need to classify/filter Team Log messages against "the human
 * lead" instead of a hardcoded "travis" literal. Pre-flight #44365/#44375:
 * no server-side helper exists yet importable from the browser (the
 * Admin-SDK-only getRosterLead() in server/api/members.ts, track:
 * roster-based-lead-identity/PR #698, can't run client-side regardless of
 * that PR's merge status) — this calls the already-live GET
 * /api/workspace/members endpoint (server.ts:206) and derives the id with
 * the same rule agentIdFromUser (src/lib/auth.ts) uses server-side-adjacent:
 * lowercased email local-part.
 *
 * Falls back to the literal "travis" — the pre-existing hardcoded behavior
 * every call site had before this hook existed — on fetch failure or when
 * no confirmed human_lead is found, so a Firestore hiccup or a workspace
 * mid-transition never breaks the UI surfaces consuming this.
 */
import { useEffect, useState } from "react";
import { getAuthHeader } from "../lib/auth";

const FALLBACK_LEAD_ID = "travis";

interface WorkspaceMember {
  email: string;
  role: "human_lead" | "human_partner";
}

function deriveAgentId(email: string): string | null {
  const local = email.trim().toLowerCase().split("@")[0];
  return local || null;
}

export function useRosterLeadId(workspaceId: string): string {
  const [leadId, setLeadId] = useState<string>(FALLBACK_LEAD_ID);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;

    async function fetchRosterLead() {
      try {
        const response = await fetch("/api/workspace/members", {
          headers: await getAuthHeader(workspaceId),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = (await response.json()) as { members?: WorkspaceMember[] };
        const lead = (data.members ?? []).find((m) => m.role === "human_lead");
        const derived = lead ? deriveAgentId(lead.email) : null;

        if (!cancelled && derived) setLeadId(derived);
      } catch (err) {
        // Fail open to the pre-existing hardcoded "travis" behavior — a
        // lookup error here should never break attention counts or chat
        // filtering, only leave them at today's default.
        console.warn("[useRosterLeadId] fetch failed, staying on fallback:", err);
      }
    }

    fetchRosterLead();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return leadId;
}
